import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { setFilters } from '../router.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  HAIRLINE,
  MOSAIC_SCALES,
  MOSAIC_SPANS,
  MOSAIC_SORTS,
  columnText,
  mosaicBackground,
  mosaicColumns,
  mosaicExportRows,
  mosaicGeometry,
  mosaicModel,
  mosaicSummary,
} from '../lib/mosaic.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** What a row of each dimension is called. Matches the heatmap's corner cell. */
const ROW_NOUNS = {
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
};

const SPAN_LABELS = { window: 'Window', week: 'Week' };
const SPAN_HINTS = {
  window: 'Every week in the window, added up',
  week: 'One week — the same one the bullet rows draw',
};
const SORT_LABELS = {
  capacity: 'Widest',
  over: 'Most over',
  allocation: 'Most loaded',
  label: 'A–Z',
};
const SCALE_LABELS = { focus: 'Focus', fit: 'Fit' };
const SCALE_HINTS = {
  focus: 'Clip the tall narrow columns so the 100% line stays readable',
  fit: 'Every column in the picture, however tall',
};

/** The plot's height in the frame's own coordinates. */
const HEIGHT = 340;

/** A column narrower than this has no room for a figure over it. The axis label under it is
 * measured against its own length instead, in `fitLabel`. */
const FIGURE_WIDTH = 26;

/**
 * The mosaic (V-7, PRD 10.3; Phase 43): the capacity heatmap as a Marimekko, so that overload
 * can be read in hours.
 *
 * Every other reading of this response is a ratio, and a ratio has no size. The matrix, the
 * multiples, the horizon and the beeswarm all put a QA role of half a person at 175 per cent
 * above a development team of ten at 110 — a darker cell, a taller mark, a higher dot — and the
 * first is eighteen hours while the second is forty. Here **width is capacity**, so the column
 * is as wide as the role is expensive; **height is allocation over capacity**, so the 100 per
 * cent line is in the same place in every column; and therefore **area is hours**, and what
 * pokes above the line is the overload in hours rather than in shade.
 *
 * Like the bullet rows, the multiples, the horizon and the beeswarm it fetches nothing: the page
 * hands it the same `GET /forecast/heatmap` response the matrix is drawn from. Over a single week its
 * columns are the bullet rows — `lib/mosaic.js` builds them from `bulletModel` — so the two
 * cards on the page cannot state different hours for the same role.
 */
class MosaicChart extends StoreMixin(LitElement) {
  static properties = {
    data: { attribute: false }, // a GET /forecast/heatmap response, or null
    loading: { type: Boolean },
    error: { type: String },
    week: { type: String }, // the week the bullet rows draw; this card follows it over a week
    _span: { state: true },
    _sort: { state: true },
    _scale: { state: true },
    _hairlines: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.data = null;
    this.loading = false;
    this.error = null;
    this.week = null;
    this._span = 'window';
    this._sort = 'capacity';
    this._scale = 'focus';
    // The labels of the columns too narrow to see, which only the measured frame knows: the
    // marks fill this in as they place the columns, and the note reads it.
    this._hairlines = [];
    this._tableOpen = false;
    this._model_ = null;
    this._modelKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
        cursor: pointer;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* ---- the marks ---- */
      .used {
        cursor: pointer;
      }
      .over {
        fill: var(--status-overloaded);
        cursor: pointer;
      }
      .headroom {
        fill: none;
        stroke: var(--border-strong, var(--border-soft));
        stroke-width: 1;
        stroke-dasharray: 3 3;
      }
      .committed {
        stroke: var(--ink-strong);
        stroke-width: 1.5;
      }
      .column[data-selected='true'] .used,
      .column[data-selected='true'] .over {
        stroke: var(--ink-strong);
        stroke-width: 2;
      }
      .column:focus-visible {
        outline: 2px solid var(--focus);
      }
      .overlabel {
        font-size: 10.5px;
        font-weight: 700;
        font-family: var(--font-sans);
        fill: var(--status-overloaded);
      }
      .name {
        font-size: 11px;
        font-family: var(--font-sans);
        fill: var(--tick-ink);
      }
      .clipmark {
        fill: none;
        stroke: var(--surface);
        stroke-width: 2;
        stroke-dasharray: 4 3;
      }

      /* ---- chrome ---- */
      .legends {
        display: grid;
        gap: 8px;
        margin-top: 12px;
      }
      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        padding: 0;
        margin: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend .sw {
        width: 22px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
        flex: none;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 26px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Derived

  /** The model, built once per response and set of choices; the card asks for it five times. */
  get _model() {
    if (!this.data) return null;
    const key = `${this._span}|${this._sort}|${this._scale}|${this.week ?? ''}`;
    if (this._modelFor !== this.data || this._modelKey !== key) {
      this._model_ = mosaicModel(this.data, {
        week: this.week,
        span: this._span,
        sort: this._sort,
        scale: this._scale,
      });
      this._modelFor = this.data;
      this._modelKey = key;
    }
    return this._model_;
  }

  get _nouns() {
    return ROW_NOUNS[this.data?.dimension] ?? ROW_NOUNS.company;
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  /**
   * Switching to a single week pulls the rest of the page onto the week this card lands on, so the
   * bullet rows beneath are the same week's hours — which is the comparison the gate makes.
   */
  _setSpan(span) {
    this._span = span;
    if (span !== 'week' || this.week) return;
    const resolved = mosaicModel(this.data, { span: 'week' })?.week?.week_start ?? null;
    if (resolved) this._selectWeek(resolved);
  }

  _selectWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Clicking a column sets the entity, so the role under the cursor travels to the other views. */
  _toggleEntity(column) {
    const { filters } = this.appState;
    const dimension = this.data?.dimension;
    if (!dimension || dimension === 'company') return;
    const already = filters.dimension === dimension && filters.entity === column.key;
    setFilters({ dimension, entity: already ? null : column.key });
  }

  _isSelected(column) {
    const { filters } = this.appState;
    return filters.dimension === this.data?.dimension && filters.entity === column.key;
  }

  _showColumn(event, model, column) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const state = STATUS_STATES.find((s) => s.key === column.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Capacity', value: exact(column.capacity_hours) },
      { label: '  Share of the chart', value: percent(column.share) },
      { label: 'Allocated', value: exact(column.allocated_hours) },
      { label: '  High confidence', value: exact(column.high_confidence_hours) },
      column.over_hours > 0
        ? { label: 'Over capacity', value: exact(column.over_hours) }
        : { label: 'Headroom', value: exact(column.headroom_hours) },
    ];
    if (model.span === 'window' && column.weeks_over > 0)
      rows.push({
        label: 'Over, week by week',
        value: `${exact(column.weeks_over_hours)} in ${count(column.weeks_over)} ${
          column.weeks_over === 1 ? 'week' : 'weeks'
        }`,
      });
    const facts = [
      columnText(column, model.thresholds),
      state ? `${state.icon} ${state.label}` : null,
      column.peak_week
        ? `peak ${percent(column.peak_utilization)}, ${weekLabel(column.peak_week)}`
        : null,
      column.clipped ? 'above the top of the scale — drawn at the ceiling' : null,
    ];
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title:
        model.span === 'week'
          ? `${column.label} · week of ${longDate(model.week.week_start)}`
          : `${column.label} · ${longDate(model.from)} to ${longDate(model.to)}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _hideTooltip() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Marks

  _marks = ({ y, plot, svg: s }) => {
    const model = this._model;
    if (!model) return nothing;
    const columns = mosaicGeometry(model, { plot, y });
    const bands = mosaicBackground(model.scale, model.thresholds, y);
    this._noteHairlines(columns);

    return s`
      <g class="bands">
        ${bands.map(
          (band) => s`<rect class="band" data-band=${band.key} x=${plot.left} width=${plot.width}
                            y=${band.y} height=${band.height}
                            fill=${`var(${band.tint})`}></rect>`,
        )}
      </g>
      <g class="columns">
        ${repeat(
          columns,
          (column) => column.key,
          (column) => s`<g
            class="column"
            data-key=${column.key}
            data-band=${column.band ?? ''}
            data-clipped=${column.clipped ? 'true' : 'false'}
            data-hairline=${column.hairline ? 'true' : 'false'}
            data-selected=${this._isSelected(column) ? 'true' : 'false'}
            tabindex="0"
            role="img"
            aria-label=${`${column.label}: ${formatHours(column.capacity_hours, {
              exact: true,
            })} of capacity, ${columnText(column, model.thresholds)}`}
            @pointermove=${(e) => this._showColumn(e, model, column)}
            @pointerleave=${() => this._hideTooltip()}
            @focus=${(e) => this._showColumn(e, model, column)}
            @blur=${() => this._hideTooltip()}
            @click=${() => this._toggleEntity(column)}
          >
            ${
              column.headroom
                ? s`<rect class="headroom" x=${column.x} width=${column.width}
                          y=${column.headroom.y} height=${column.headroom.height}></rect>`
                : nothing
            }
            <rect class="used" data-part="used" x=${column.x} width=${column.width}
                  y=${column.over ? column.capacity_y : column.y}
                  height=${column.over ? column.height - column.over.height : column.height}
                  fill=${`var(--status-${column.band ?? 'healthy'})`}></rect>
            ${
              column.over
                ? s`<rect class="over" data-part="over" x=${column.x} width=${column.width}
                          y=${column.over.y} height=${column.over.height}></rect>`
                : nothing
            }
            ${
              column.committed_y !== null && column.committed_y > column.y
                ? s`<line class="committed" x1=${column.x} x2=${column.x + column.width}
                          y1=${column.committed_y} y2=${column.committed_y}></line>`
                : nothing
            }
            ${
              column.clipped
                ? s`<line class="clipmark" x1=${column.x} x2=${column.x + column.width}
                          y1=${column.y + 3} y2=${column.y + 3}></line>`
                : nothing
            }
          </g>`,
        )}
      </g>
      <g class="figures">
        ${columns.map((column) =>
          column.over_hours > 0 && column.width >= FIGURE_WIDTH
            ? s`<text class="overlabel" data-key=${column.key}
                      x=${column.centre} y=${column.y - 4}
                      text-anchor="middle">${formatHours(column.over_hours, { unit: false })} h</text>`
            : nothing,
        )}
      </g>
      <g class="names">
        ${columns.map((column) =>
          column.axis_label
            ? s`<text class="name" data-key=${column.key}
                      x=${column.centre} y=${plot.top + plot.height + 14}
                      text-anchor="middle">${column.axis_label}<title>${column.label}</title></text>`
            : nothing,
        )}
      </g>
    `;
  };

  /**
   * A column's width is known only once the frame has measured the page, and the card has a note
   * to write about the ones too narrow to see. The marks report them back; the note re-renders
   * once and then the labels stop changing, so this settles rather than loops.
   */
  _noteHairlines(columns) {
    const labels = columns.filter((c) => c.hairline).map((c) => c.label);
    if (labels.join('|') !== this._hairlines.join('|')) this._hairlines = labels;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    return html`
      <div class="controls">
        <div class="segmented" role="group" aria-label="Span">
          <span>Over</span>
          <span class="group">
            ${MOSAIC_SPANS.map(
              (key) =>
                html`<button
                  type="button"
                  data-span=${key}
                  title=${SPAN_HINTS[key]}
                  aria-pressed=${this._span === key ? 'true' : 'false'}
                  @click=${() => this._setSpan(key)}
                >
                  ${SPAN_LABELS[key]}
                </button>`,
            )}
          </span>
        </div>
        <div class="segmented" role="group" aria-label="Order">
          <span>Order</span>
          <span class="group">
            ${MOSAIC_SORTS.map(
              (key) =>
                html`<button
                  type="button"
                  data-sort=${key}
                  aria-pressed=${this._sort === key ? 'true' : 'false'}
                  @click=${() => (this._sort = key)}
                >
                  ${SORT_LABELS[key]}
                </button>`,
            )}
          </span>
        </div>
        <div class="segmented" role="group" aria-label="Value scale">
          <span>Scale</span>
          <span class="group">
            ${MOSAIC_SCALES.map(
              (key) =>
                html`<button
                  type="button"
                  data-scale=${key}
                  title=${SCALE_HINTS[key]}
                  aria-pressed=${this._scale === key ? 'true' : 'false'}
                  @click=${() => (this._scale = key)}
                >
                  ${SCALE_LABELS[key]}
                </button>`,
            )}
          </span>
        </div>
      </div>
    `;
  }

  _legend() {
    return html`
      <div class="legends">
        <ul class="legend" aria-label="What the areas and the marks mean">
          ${STATUS_STATES.map(
            (state) =>
              html`<li>
                <span
                  class="sw"
                  style=${`background: var(${state.var});`}
                  aria-hidden="true"
                ></span>
                <span>${state.icon} ${state.label}</span>
              </li>`,
          )}
          <li>
            <svg width="22" height="12" aria-hidden="true">
              <rect
                x="0.5"
                y="0.5"
                width="21"
                height="11"
                fill="none"
                stroke="var(--border-soft)"
                stroke-dasharray="3 3"
              ></rect>
            </svg>
            <span>Capacity nobody is using — the area is the free hours</span>
          </li>
          <li>
            <svg width="22" height="8" aria-hidden="true">
              <line x1="0" x2="22" y1="4" y2="4" stroke="var(--ink-strong)" stroke-width="2"></line>
            </svg>
            <span>How high the high-confidence hours alone would reach</span>
          </li>
        </ul>
      </div>
    `;
  }

  _kpis(model) {
    const s = mosaicSummary(model);
    const worst = s.worst;
    const widest = s.widest;
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">
            ${s.weeks_over_hours > 0 ? formatHours(s.weeks_over_hours, { unit: false }) : '—'}
          </div>
          <div class="l">
            ${
              s.weeks_over_hours > 0
                ? `hours over capacity, ${count(s.columns_over)} of ${count(s.columns)} ${
                    this._nouns[1]
                  }`
                : `no ${this._nouns[1]} over capacity`
            }
          </div>
        </div>
        <div class="kpi">
          <div class="n">
            ${worst && worst.weeks_over_hours > 0 ? formatHours(worst.weeks_over_hours, { unit: false }) : '—'}
          </div>
          <div class="l">
            ${
              worst && worst.weeks_over_hours > 0
                ? `the worst of them — ${worst.label}, ${percent(worst.share)} of the capacity`
                : `widest: ${widest ? widest.label : '—'}`
            }
          </div>
        </div>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const when =
      model.span === 'week'
        ? `week of ${weekLabel(model.week.week_start)}`
        : `${count(model.weeks.length)} weeks`;
    return `one column per ${this._nouns[0].toLowerCase()}, width its capacity · ${count(
      model.columns.length,
    )} ${this._nouns[1]} over ${when} · ${SCALE_LABELS[model.scale.mode].toLowerCase()} scale to ${percent(
      model.scale.max,
    )} · pipeline ${this.data.pipeline_mode}`;
  }

  _notes(model) {
    const { filters } = this.appState;
    const s = mosaicSummary(model);
    const notes = [];
    const worst = s.worst;
    const widest = s.widest;
    notes.push(
      html`<p class="note" data-note="why">
        Width is capacity and height is allocation, so <strong>area is hours</strong> and what pokes
        above the 100% line is the overload in hours rather than in shade.
        ${
          worst && widest && worst.key !== widest.key && worst.weeks_over_hours > 0
            ? html`${worst.label} is the reddest ${this._nouns[0].toLowerCase()} here at
              ${percent(worst.peak_utilization)} in its worst week, and it is
              ${percent(worst.share)} of the capacity —
              ${formatHours(worst.weeks_over_hours, { exact: true })} over. ${widest.label} is
              ${percent(widest.share)} of it.`
            : html`A narrow column cannot cost much however red it is, which is the comparison the
              matrix cannot make.`
        }
      </p>`,
    );
    if (model.span === 'window' && s.weeks_over_hours > s.over_hours)
      notes.push(
        html`<p class="note" data-note="netting">
          Over the window the area above the line can only show the
          ${formatHours(s.over_hours, { exact: true })} that do not cancel out: a
          ${this._nouns[0].toLowerCase()} over in one week and spare in the next nets to nothing in
          the geometry. Counted week by week — which is what anybody has to staff for — it is
          ${formatHours(s.weeks_over_hours, { exact: true })}. Switch the span to
          <strong>Week</strong> for a picture where the two are the same number.
        </p>`,
      );
    if (model.span === 'week')
      notes.push(
        html`<p class="note" data-note="week">
          The columns are the bullet rows beneath, drawn as area instead of length: same week, same
          hours, same rows — the hours over a column states is the figure on the row below it.
        </p>`,
      );
    if (s.unstaffed_columns > 0)
      notes.push(
        html`<p class="note" data-note="unstaffed">
          ${count(s.unstaffed_columns)}
          ${s.unstaffed_columns === 1 ? `${this._nouns[0].toLowerCase()} carries` : `${this._nouns[1]} carry`}
          ${formatHours(s.unstaffed_hours, { exact: true })} with nobody staffed to
          ${s.unstaffed_columns === 1 ? 'it' : 'them'}: no capacity is no width, so there is no
          column to draw. Those hours are in the table, in the matrix and in the company's own
          figure — they are not overload, they are work nobody is named for.
        </p>`,
      );
    const hairlines = this._hairlines;
    if (hairlines.length)
      notes.push(
        html`<p class="note" data-note="hairline">
          ${count(hairlines.length)} ${hairlines.length === 1 ? 'column is' : 'columns are'} a
          hairline — ${HAIRLINE} pixels or less, because that is what their share of the capacity
          comes to. They are never widened: a wider column would be a claim about money that is not
          true. ${hairlines.join(', ')} — the figures are in the table.
        </p>`,
      );
    if (s.clipped > 0)
      notes.push(
        html`<p class="note" data-note="clipped">
          ${count(s.clipped)} ${s.clipped === 1 ? 'column runs' : 'columns run'} past
          ${percent(model.scale.max)} and ${s.clipped === 1 ? 'is' : 'are'} drawn at the ceiling
          with a broken top, so ${s.clipped === 1 ? 'its' : 'their'} area understates the hours.
          Switch the scale to <strong>Fit</strong> for the true height, or read the hours in the
          tooltip and the table.
        </p>`,
      );
    if (this.data.dimension === 'individual')
      notes.push(
        html`<p class="note" data-note="initials">
          The label under a column is the person's initials — a name has never fitted under a column
          this narrow. The whole name is on the column itself: hover it, or read the table.
        </p>`,
      );
    if (this.data.dimension === 'company')
      notes.push(
        html`<p class="note" data-note="dimension">
          V-7 is a chart of roles: set the dimension to <strong>Service</strong> or
          <strong>Department</strong> and every column is one of them, as wide as it is expensive.
          At the company dimension there is one column and nothing to compare it with.
        </p>`,
      );
    if (this.data.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(this.data.withheld_count)}
          ${this.data.withheld_count === 1 ? 'row is' : 'rows are'} not shown: your access covers
          yourself and your reports at the individual grain. Their capacity is not in the widths, so
          the shares here are shares of what you can see.
        </p>`,
      );
    if (filters.entity && filters.dimension === this.data.dimension) {
      const column = model.columns.find((c) => c.key === filters.entity);
      notes.push(
        html`<p class="note" data-note="entity">
          ${
            column
              ? `${column.label}'s column is outlined;`
              : 'The entity in the filter bar is not a column here;'
          }
          every column stays in the chart, because a mosaic of one is a bullet row and there is one
          of those below. Click the column again to clear the filter.
        </p>`,
      );
    }
    notes.push(
      html`<p class="note" data-note="how">
        Click a column to filter every view to that ${this._nouns[0].toLowerCase()}. The dark rule
        inside a column is how high its high-confidence hours alone would reach: a column over the
        line on that rule is over capacity on work that is already booked.
      </p>`,
    );
    return notes;
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/mosaic.js).
    return withFormats(mosaicColumns({ dimension: this.data?.dimension }), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        peak_week: (v) => (v ? weekLabel(v) : '—'),
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
      },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this.error) body = html`<div class="state error">${this.error}</div>`;
    else if (!this.data && this.loading) body = html`<div class="state">Loading the window…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else if (!model.columns.length)
      body = html`
        ${this._controls()}
        <div class="state" data-note="nobody">
          Nobody is staffed to anything here, so there is no capacity to give a column its width.
          ${
            model.unstaffed.length
              ? html`The ${formatHours(mosaicSummary(model).unstaffed_hours, { exact: true })} of
                demand are real and are in the matrix, the bullet rows and the table.`
              : nothing
          }
        </div>
      `;
    else
      body = html`
        ${this._controls()}
        <chart-frame
          .categories=${[]}
          .max=${model.scale.max}
          .marks=${this._marks}
          .formatValue=${(v) => percent(v)}
          .rule=${model.thresholds.caution_above}
          rule-label="Capacity"
          value-label="Allocation"
          category-label=${
            this.data.dimension === 'individual'
              ? 'Person (initials), as wide as their capacity'
              : `${this._nouns[0]}, as wide as its capacity`
          }
          height=${HEIGHT}
        ></chart-frame>
        ${this._legend()} ${this._notes(model)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`One row per ${this._nouns[0].toLowerCase()} — ${this._subtitle(model)}`}
            export-name="mosaic"
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${mosaicExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this.loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Mosaic</div>
            <h2>Where is the overload big, and not just red?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${model ? this._kpis(model) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('mosaic-chart', MosaicChart);
